import type { ComponentProps, ReactNode } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { Textarea } from '../textarea';
import { cn } from '../../lib/utils';
import { FieldDescription, FieldLabelRow } from './field-label-row';
import { CharacterCount, type RecommendedLength } from './character-count';

export default function TextareaField({
	label,
	className,
	optional,
	info,
	description,
	recommendedLength,
	...props
}: ComponentProps<'textarea'> & {
	label?: string;
	optional?: boolean;
	info?: ReactNode;
	description?: ReactNode;
	/** Show a live character counter with color-coded feedback against a recommended range. */
	recommendedLength?: RecommendedLength;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isReadOnly = props.readOnly ?? formReadOnly;
	const isOptional = useIsFieldOptional(field.name, optional);

	return (
		<div className={className}>
			<div className="grid gap-2">
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						info={info}
					/>
				)}
				{description && <FieldDescription>{description}</FieldDescription>}
				<Textarea
					id={field.name}
					name={field.name}
					aria-invalid={hasFieldError(field.state.meta)}
					value={isReadOnly && !field.state.value ? '-' : (field.state.value ?? '')}
					onBlur={field.handleBlur}
					onChange={(e) => field.handleChange(e.target.value)}
					className={cn(
						'min-h-24',
						isReadOnly && 'border-transparent px-0 focus-visible:ring-0 focus-visible:border-transparent',
					)}
					{...props}
					readOnly={isReadOnly}
					tabIndex={isReadOnly ? -1 : undefined}
				/>
			</div>
			{recommendedLength && <CharacterCount value={field.state.value ?? ''} recommended={recommendedLength} />}
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
